/**
 * 메모리 ring buffer 로그 수집기 — 도메인/플랫폼 무관 순수 TS.
 *
 * console(log/warn/error) + window `error`/`unhandledrejection`을 가로채 메모리에 누적한다.
 * 문의하기(고객 지원) 시점에 `getLog(meta)`로 디바이스 정보 헤더 + 최근 로그 본문을 한 문자열로
 * 뽑아 메일/share에 첨부한다.
 *
 * 디바이스 정보(platform/version/userAgent 등)는 패키지가 직접 읽지 않고 호출자가 {@link DeviceMeta}로
 * 주입한다(DIP — navigator/Capacitor 의존 0이라 다른 프로젝트·테스트에서 재사용 가능).
 *
 * `@yjsgames/autoplay`의 consoleCapture와 **공존**한다 — 둘 다 console을 래핑하지만 각자 원본
 * (`orig.apply`)을 호출하므로 이중 래핑이 안전하다. console.warn으로 나가는 `@yjsgames/diag-log`
 * 로그도 자동 캡처된다.
 */

/** getLog 헤더에 박을 디바이스/세션 정보 — 호출자가 주입(패키지는 환경 API 의존 0). */
export interface DeviceMeta {
  /** "android" | "ios" | "web" 등 — Capacitor.getPlatform() 결과. */
  readonly platform: string;
  /** 앱 버전 (빌드 포함 가능, 예: "1.2.0(34)"). */
  readonly appVersion: string;
  /** navigator.userAgent (없으면 "" 가능). */
  readonly userAgent: string;
  /** 표시 언어 (예: "ko"). */
  readonly language: string;
  /** 익명/플랫폼 계정 uid — 있으면 헤더에 포함(문의 추적용). */
  readonly userId?: string;
  /** ISO 시각 등 호출자가 주입(주입 없으면 패키지가 `new Date()`로 채움 — 테스트 결정성 위해 주입 권장). */
  readonly timestamp?: string;
}

/** 가로챌 console 레벨. */
export type LogLevel = "log" | "warn" | "error";

export interface LogCollectorOptions {
  /** ring buffer 총 char 상한 — 초과 시 가장 오래된 라인부터 제거. 기본 256KB. */
  readonly maxChars?: number;
  /** 가로챌 console 메서드. 기본 ["log", "warn", "error"]. */
  readonly levels?: readonly LogLevel[];
}

export interface LogCollector {
  /** console + window 리스너 설치. uninstall 함수 반환. 중복 설치는 무시(idempotent). */
  install(): () => void;
  /** 디바이스 헤더 + ring buffer 본문을 한 문자열로 반환. 대기 중인 dedup 라인을 flush한다. */
  getLog(meta: DeviceMeta): string;
  /** 버퍼 비우기. */
  clear(): void;
}

const DEFAULT_MAX_CHARS = 256 * 1024;
const DEFAULT_LEVELS: readonly LogLevel[] = ["log", "warn", "error"];

/**
 * ring buffer 로그 수집기를 만든다. {@link LogCollector.install}을 호출하기 전까지 아무 것도 가로채지 않는다.
 */
export function createLogCollector(opts: LogCollectorOptions = {}): LogCollector {
  const maxChars = opts.maxChars ?? DEFAULT_MAX_CHARS;
  const levels = opts.levels ?? DEFAULT_LEVELS;

  // ring buffer — 라인 배열 + 누적 char 수(상한 초과 시 앞에서 제거).
  const lines: string[] = [];
  let totalChars = 0;
  // 연속 동일 라인 접기.
  let lastLine: string | null = null;
  let repeats = 0;

  let installed = false;

  const pushLine = (line: string): void => {
    lines.push(line);
    totalChars += line.length + 1; // +1 = 줄바꿈
    while (totalChars > maxChars && lines.length > 1) {
      const dropped = lines.shift();
      if (dropped !== undefined) totalChars -= dropped.length + 1;
    }
  };

  /** 대기 중인 반복(`(repeated xN)`)을 ring buffer로 내보낸다. */
  const flushRepeats = (): void => {
    if (repeats > 0 && lastLine !== null) {
      pushLine(`(repeated x${repeats}) ${lastLine}`);
      repeats = 0;
    }
  };

  /** 한 라인 기록 — 직전과 동일하면 접고, 다르면 직전 반복을 flush 후 추가. */
  const record = (line: string): void => {
    if (line === lastLine) {
      repeats += 1;
      return;
    }
    flushRepeats();
    lastLine = line;
    pushLine(line);
  };

  const fmt = (level: string, args: readonly unknown[]): string => {
    const ts = new Date().toISOString().substring(11, 19); // HH:MM:SS
    return `[${ts}] [${level}] ${args.map(argToString).join(" ")}`;
  };

  return {
    install(): () => void {
      if (installed) return noop;
      installed = true;

      const originals = new Map<LogLevel, (...args: unknown[]) => void>();
      for (const level of levels) {
        const orig = console[level] as (...args: unknown[]) => void;
        originals.set(level, orig);
        console[level] = (...args: unknown[]): void => {
          orig.apply(console, args);
          record(fmt(level, args));
        };
      }

      const onError = (e: ErrorEvent): void => {
        const where = e.filename ? ` (${e.filename}:${e.lineno}:${e.colno})` : "";
        const stack = e.error instanceof Error && e.error.stack ? `\n${e.error.stack}` : "";
        record(fmt("uncaught", [`${e.message}${where}${stack}`]));
      };
      const onRejection = (e: PromiseRejectionEvent): void => {
        const reason = e.reason;
        const text =
          reason instanceof Error
            ? (reason.stack ?? `${reason.name}: ${reason.message}`)
            : String(reason);
        record(fmt("unhandledrejection", [text]));
      };
      const hasWindow = typeof window !== "undefined";
      if (hasWindow) {
        window.addEventListener("error", onError);
        window.addEventListener("unhandledrejection", onRejection);
      }

      return (): void => {
        for (const [level, orig] of originals) {
          console[level] = orig as never;
        }
        if (hasWindow) {
          window.removeEventListener("error", onError);
          window.removeEventListener("unhandledrejection", onRejection);
        }
        installed = false;
      };
    },

    getLog(meta: DeviceMeta): string {
      flushRepeats();
      const time = meta.timestamp ?? new Date().toISOString();
      const header = [
        `[INFO] platform: ${meta.platform}`,
        `[INFO] appVersion: ${meta.appVersion}`,
        `[INFO] userAgent: ${meta.userAgent}`,
        `[INFO] language: ${meta.language}`,
        ...(meta.userId ? [`[INFO] userId: ${meta.userId}`] : []),
        `[INFO] time: ${time}`,
        "------------------------------",
      ].join("\n");
      return `${header}\n${lines.join("\n")}`;
    },

    clear(): void {
      lines.length = 0;
      totalChars = 0;
      lastLine = null;
      repeats = 0;
    },
  };
}

function noop(): void {
  /* idempotent install — 이미 설치됨. */
}

/** 임의 인자를 로그 라인용 문자열로 — string 그대로, Error는 stack, 객체는 JSON, 실패 시 String(). */
export function argToString(a: unknown): string {
  if (typeof a === "string") return a;
  if (a instanceof Error) return a.stack ?? `${a.name}: ${a.message}`;
  try {
    return JSON.stringify(a);
  } catch {
    return String(a);
  }
}
